/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// Boost-inquire button handler - uses event delegation to handle clicks on .boost-inquire buttons
// Also handles app-rendered buttons with inquiry data attributes
(function() {
    // Helper function to check if an element is an inquiry button
    function isInquiryButton(element) {
        if (!element || !element.dataset) {
            return false;
        }
        
        // Must have data-id (required)
        if (!element.dataset.id) {
            return false;
        }
        
        // Must be a clickable element (button, link, or have inquiry-related data attributes)
        const isClickable = element.tagName === 'BUTTON' || 
                           element.tagName === 'A' || 
                           element.getAttribute('role') === 'button' ||
                           element.onclick !== null;
        
        // Must have at least one other inquiry-related attribute OR be within app section
        const hasInquiryAttributes = element.dataset.title || 
                                    element.dataset.sku || 
                                    element.dataset.image ||
                                    element.closest('.shopify-section--apps');
        
        return isClickable && hasInquiryAttributes;
    }

    // Use event delegation - listen for clicks on the document
    // Use capture phase to catch events before they bubble and before other handlers
    document.addEventListener('click', function(e) {
        let button = null;
        
        // First, try to find .boost-inquire class (handles clicks on child elements too)
        button = e.target.closest('.boost-inquire');
        
        // If not found, check if clicked element or parent has inquiry data attributes
        // This handles app-rendered buttons that might not have the class
        if (!button) {
            let element = e.target;
            
            // Walk up the DOM tree to find an inquiry button
            while (element && element !== document.body) {
                if (isInquiryButton(element)) {
                    button = element;
                    break;
                }
                element = element.parentElement;
            }
        }
        
        if (!button) {
            return; // Not an inquiry button
        }

        // Get product data from button's data attributes
        const product = {
            id: button.dataset.id,
            title: button.dataset.title || '',
            image: button.dataset.image || '',
            sku: button.dataset.sku || '',
            quantity: parseInt(button.dataset.quantity) || 1,
            url: button.dataset.url || window.location.href
        };

        // Validate required data
        if (!product.id) {
            console.warn('boost-inquire: Product ID is missing', button);
            return; // Not a valid inquiry button
        }

        // Prevent default immediately to stop navigation/link behavior
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        // Handle different button types
        if (button.tagName === 'A' && button.href) {
            // If it's a link, remove href temporarily to prevent navigation
            const originalHref = button.href;
            button.href = 'javascript:void(0)';
            // Restore after a short delay
            setTimeout(() => {
                if (button.href === 'javascript:void(0)') {
                    button.href = originalHref;
                }
            }, 100);
        } else if (button.tagName === 'BUTTON') {
            // If it's a button, ensure it doesn't submit a form
            if (button.type === 'submit' || !button.type) {
                button.type = 'button';
            }
            // Find and prevent form submission if button is in a form
            const form = button.closest('form');
            if (form) {
                form.addEventListener('submit', function(evt) {
                    evt.preventDefault();
                    evt.stopPropagation();
                }, { once: true, capture: true });
            }
        }

        // Dispatch the addToInquiry event with bubbles: true to ensure it propagates
        const inquiryEvent = new CustomEvent('addToInquiry', {
            detail: product,
            bubbles: true,
            cancelable: true
        });
        
        console.log('Dispatching addToInquiry event:', product);
        const dispatched = document.dispatchEvent(inquiryEvent);
        
        if (!dispatched) {
            console.warn('addToInquiry event was prevented');
            return;
        }
        
        // Update button state if it's on a collection page (boost-sd__product-item)
        // Wait a moment for the storage to be updated
        setTimeout(() => {
            const productItem = button.closest('.boost-sd__product-item');
            if (productItem && button.tagName === 'BUTTON') {
                button.disabled = true;
                button.textContent = (window.themeVariables && window.themeVariables.strings && window.themeVariables.strings.inquiryAdded) || 'Tilføjet!';
                button.setAttribute('aria-disabled', 'true');
                button.classList.add('boost-inquire--added');
            }
        }, 100);
    }, true); // Use capture phase (true) to catch events earlier
})();

// Add inquiry buttons to Boost AI Search product cards on collection pages
(function() {
    // Function to extract product data from a product card
    function extractProductData(linkElement) {
        // Find the product item container
        const productItem = linkElement.closest('.boost-sd__product-item');
        if (!productItem) {
            return null;
        }
        
        // Get product ID from data-product-id or id attribute
        const productId = productItem.dataset.productId || productItem.id || null;
        
        // Get product URL from the link
        const productUrl = linkElement.href || linkElement.getAttribute('href') || '';
        
        // Parse data-product JSON (it has HTML entities, so we need to decode)
        let productData = null;
        const dataProduct = productItem.getAttribute('data-product');
        if (dataProduct) {
            try {
                // Decode HTML entities and parse JSON
                const decoded = dataProduct.replace(/&quot;/g, '"').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>');
                productData = JSON.parse(decoded);
            } catch (e) {
                console.warn('Failed to parse data-product:', e);
            }
        }
        
        // Get product title from .boost-sd__product-title
        let productTitle = '';
        const titleElement = productItem.querySelector('.boost-sd__product-title');
        if (titleElement) {
            productTitle = titleElement.textContent.trim();
        }
        
        // Get product image - try from data-product JSON first, then from img element
        let productImage = '';
        if (productData && productData.images && productData.images.length > 0) {
            productImage = productData.images[0].src;
        } else {
            const imageElement = productItem.querySelector('img.boost-sd__product-image-img');
            if (imageElement) {
                productImage = imageElement.src || imageElement.getAttribute('src') || '';
            }
        }
        
        // Get SKU from .boost-sd__product-sku
        let productSku = '';
        const skuElement = productItem.querySelector('.boost-sd__product-sku');
        if (skuElement) {
            productSku = skuElement.textContent.trim();
        }
        
        // Extract priceMax from productData if available
        const priceMax = productData && typeof productData.priceMax !== 'undefined'
            ? productData.priceMax
            : null;
        
        return {
            id: productId,
            title: productTitle,
            image: productImage,
            sku: productSku,
            url: productUrl,
            priceMax: priceMax
        };
    }
    
    // Function to check if product is already in inquiry list
    function isProductInInquiry(productId, productSku) {
        try {
            // Access the global getSavedProducts function from theme.js
            if (typeof getSavedProducts === 'function') {
                const savedProducts = getSavedProducts();
                return savedProducts.some(p => 
                    (productId && String(p.id) === String(productId)) || 
                    (productSku && p.sku === productSku)
                );
            }
        } catch (e) {
            console.warn('Could not check inquiry list:', e);
        }
        return false;
    }
    
    // Function to create an inquiry button
    function createInquiryButton(productData) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'boost-inquire inquire_form_btn button button--primary';
        
        // Set data attributes
        if (productData.id) button.dataset.id = productData.id;
        if (productData.title) button.dataset.title = productData.title;
        if (productData.image) button.dataset.image = productData.image;
        if (productData.sku) button.dataset.sku = productData.sku;
        if (productData.url) button.dataset.url = productData.url;
        button.dataset.quantity = '1';
        
        // Check if product is already in inquiry list
        const isAlreadyAdded = isProductInInquiry(productData.id, productData.sku);
        if (isAlreadyAdded) {
            button.disabled = true;
            button.textContent = (window.themeVariables && window.themeVariables.strings && window.themeVariables.strings.inquiryAdded) || 'Tilføjet!';
            button.setAttribute('aria-disabled', 'true');
            button.classList.add('boost-inquire--added');
        } else {
            button.textContent = (window.themeVariables && window.themeVariables.strings && window.themeVariables.strings.inquiryAddToInquiry) || 'Tilføj til forespørgsel';
        }
        
        return button;
    }
    
    // Function to add buttons to existing product links
    function addInquiryButtons() {
        // Find all product items
        const productItems = document.querySelectorAll('.boost-sd__product-item');
        
        productItems.forEach(productItem => {
            // Find the second product link (the one with product info, not the image link)
            // This is the link that wraps the product-info div
            const productInfoLink = productItem.querySelector('a.boost-sd__product-link[aria-labelledby]');
            
            if (!productInfoLink) {
                return; // No product info link found
            }
            
            // Check if button already exists after this link
            const existingButton = productInfoLink.parentElement.querySelector('.boost-inquire');
            if (existingButton) {
                return; // Button already exists
            }
            
            // Extract product data
            const productData = extractProductData(productInfoLink);
            
            if (!productData || (!productData.id && !productData.url)) {
                return; // No valid product data
            }
            
            // Decide visibility based on login state:
            // - If user is logged in: only show for free products (priceMax == 0)
            // - If user is NOT logged in: show for all products
            const isLoggedIn = typeof window.customerIsLoggedIn !== 'undefined' ? window.customerIsLoggedIn : false;
            const priceMax = productData.priceMax;
            const isFreeProduct = (priceMax === 0 || priceMax === '0' || priceMax === 0.0 || priceMax === '0.0');

            if (isLoggedIn && !isFreeProduct) {
                return;
            }
            
            // Create and insert button
            const button = createInquiryButton(productData);
            
            // Insert button after the link (which wraps the product-info div)
            // The link is inside the product-item, so we insert after the link closes
            productInfoLink.parentNode.insertBefore(button, productInfoLink.nextSibling);
            
            // Hide the "Add to cart" button and price element
            const addToCartButton = productItem.querySelector('.boost-sd__button--select-option');
            if (addToCartButton) {
                addToCartButton.style.display = 'none';
            }
            
            const priceWrapper = productItem.querySelector('.boost-sd__product-price-wrapper');
            if (priceWrapper) {
                priceWrapper.style.display = 'none';
            }
        });
    }
    
    // Run on DOM ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', addInquiryButtons);
    } else {
        addInquiryButtons();
    }
    
    // Watch for dynamically added product cards (for AJAX-loaded content)
    if (typeof MutationObserver !== 'undefined') {
        const observer = new MutationObserver(function(mutations) {
            let shouldAddButtons = false;
            
            mutations.forEach(function(mutation) {
                mutation.addedNodes.forEach(function(node) {
                    if (node.nodeType === 1) { // Element node
                        // Check if the added node or its children contain product links
                        if (node.classList && node.classList.contains('boost-sd__product-link')) {
                            shouldAddButtons = true;
                        } else if (node.querySelectorAll && node.querySelectorAll('a.boost-sd__product-link').length > 0) {
                            shouldAddButtons = true;
                        }
                    }
                });
            });
            
            if (shouldAddButtons) {
                // Use setTimeout to ensure DOM is fully updated
                setTimeout(addInquiryButtons, 100);
            }
        });
        
        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    }
})();